import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import Logger from '../../../common/logger';
import {
    AdminProduct,
    useRetireProductMutation,
} from '../../../api/group/groupAdminApiSlice';
import CustomButton from '../../../components/CustomButton';
import { enqueueSuccessSnackbar } from '../../../action/snackbarCommands';
import CustomModal from '../../common/CustomModal';
import {
    productRetireOutcome,
    retireFailureFrom,
} from '../productRetireOutcome';

interface Props {
    product: AdminProduct;
    onClose: () => void;
}

/**
 * Confirm and run a product retire.
 *
 * ── Why a dialog rather than the shared ConfirmationDialog ─────────────────
 *
 * `components/ConfirmationDialog` renders one body string out of Redux and has
 * no place to put a *result*. This action's refusal is the interesting part —
 * the server answers 409 with the list of reasons, and the operator needs to
 * read them and then act (unlink the objects, cancel the booking) before the
 * retire can succeed. Pushing that back through a global content string would
 * mean either dropping the reasons or encoding them into one sentence, which
 * is the "one message for every cause" failure #2255 already cost us a round.
 *
 * So the dialog stays open on a refusal and shows the reasons in place, and
 * closes only on success.
 */
const ProductRetireDialog = ({ product, onClose }: Props) => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [retireProduct, { isLoading }] = useRetireProductMutation();
    const [reasonKeys, setReasonKeys] = useState<string[]>([]);
    const [detail, setDetail] = useState<string | null>(null);

    const name = product.name ?? t('common.missing');

    const handleRetire = async () => {
        setReasonKeys([]);
        setDetail(null);
        try {
            const result = await retireProduct({ productId: product.id }).unwrap();
            // `retired: false` is a 200 — the row was already archived, which
            // is a stale list rather than a failure. Say which of the two
            // happened instead of reporting both as "done".
            dispatch(enqueueSuccessSnackbar({
                message: result.retired
                    ? t('services.products.retire.success', { name })
                    : t('services.products.retire.already_retired', { name }),
                autoHideDuration: 4000,
            }));
            onClose();
        } catch (err) {
            Logger('ProductRetireDialog').error(`Product retire failed: ${JSON.stringify(err)}`);
            const outcome = productRetireOutcome(retireFailureFrom(err));
            setReasonKeys(outcome.reasonKeys);
            setDetail(outcome.detail ?? null);
        }
    };

    const hasFailure = reasonKeys.length > 0 || !!detail;

    return (
        <CustomModal
            open={true}
            title={t('services.products.retire.confirm_title')}
            description={name}
            onClose={onClose}
            closeButton={
                <CustomButton
                    title={t('services.products.retire.cancel')}
                    isFilled={false}
                    onClick={onClose}
                />
            }
            saveButton={
                <CustomButton
                    title={t('services.products.retire.confirm_cta')}
                    loading={isLoading}
                    disabled={isLoading}
                    onClick={handleRetire}
                />
            }
        >
            <div className={'flex flex-col gap-4'}>
                <p className={'text-sm text-foreground/70'}>
                    {t('services.products.retire.confirm_body', { name })}
                </p>

                {hasFailure && (
                    <div
                        className={'rounded-[5px] border border-foreground/15 bg-foreground/5'
                            + ' px-4 py-3 text-sm text-foreground/70'}
                        role={'alert'}
                    >
                        <p className={'font-medium text-foreground mb-1'}>
                            {t('services.products.retire.failed_title', { name })}
                        </p>
                        {reasonKeys.length > 0 && (
                            <ul className={'list-disc pl-5 flex flex-col gap-1'}>
                                {reasonKeys.map((key) => (
                                    <li key={key}>
                                        {t(`services.products.retire.${key}`)}
                                    </li>
                                ))}
                            </ul>
                        )}
                        {/* The server's own words, for the shapes the
                            classifier does not name — never a raw key, never a
                            silent nothing. */}
                        {detail && <p>{detail}</p>}
                    </div>
                )}
            </div>
        </CustomModal>
    );
};

export default ProductRetireDialog;
